<template>
  <BasicTransition
    :appear="animateAppear"
    :enabled="enabled"
    :move-class="enabled ? $style['move'] : undefined"
    @appear="enter"
    @enter="enter"
    @leave="leave"
    @appear-cancelled="cancel"
    @enter-cancelled="cancel"
    @leave-cancelled="cancel"
  >
    <slot
      name="default"
      v-bind="$attrs"
    />
  </BasicTransition>
</template>

<script lang="ts" setup>
import type { AnimationControls } from 'motion'
import { animate } from 'motion'
import { transitionProps } from '../transitionProps'
import { normalizeTimingInput } from '../normalizeTimingInput'
import BasicTransition from '../BasicTransition'

const props = defineProps({
  ...transitionProps,
})

let animation: AnimationControls

async function enter(el: HTMLElement, done: () => void) {
  if (!props.enabled) {
    return done()
  }

  if (el?.parentNode?.children === undefined) {
    return
  }

  const index = [...el.parentNode.children].indexOf(el)
  const duration = props.duration
  const initialDelay = props.delay !== undefined ? props.delay : duration
  const delay = Math.min(props.maxDelay, initialDelay + index * 0.1)

  if (delay === props.maxDelay) {
    done()
    return
  }

  animation = animate(
    el,
    {
      opacity: [0, 1],
      transform: ['translateY(10px)', 'translateY(0px)'],
    },
    {
      duration: normalizeTimingInput(duration),
      delay: normalizeTimingInput(delay),
      easing: 'ease-out',
    },
  )

  await animation.finished
  el.style.removeProperty('transform')
  el.style.removeProperty('opacity')
  done()
}

async function leave(el: HTMLElement, done: () => void) {
  if (!props.enabled) {
    return done()
  }

  if (!props.animateExit) {
    return done()
  }

  if (el?.parentNode?.children === undefined) {
    return
  }

  const index = [...el.parentNode.children].indexOf(el)
  const duration = props.duration
  const initialDelay = props.delay !== undefined ? props.delay : duration
  const delay = Math.min(props.maxDelay, initialDelay + index * 0.1)

  animation = animate(
    el,
    {
      opacity: [1, 0],
      transform: ['translateY(0px)', 'translateY(10px)'],
    },
    {
      duration: normalizeTimingInput(duration),
      delay: normalizeTimingInput(delay),
      easing: 'ease-out',
    },
  )

  await animation.finished
  done()
}

function cancel() {
  animation?.cancel?.()
}
</script>

<style lang="postcss" module>
.move {
  transition: all 0.75s ease;
}
</style>
